import {app} from './firebase.js';
import {getAuth, signInAnonymously} from 'firebase/auth';
import {getFirestore, collection, addDoc} from 'firebase/firestore';
import {getAnalytics, logEvent} from 'firebase/analytics';
import {detect} from 'detect-browser';
import {getAppEnv} from 'features/HelperFunctions.js';

function getFirebaseCollection(url) {
  const appEnv = getAppEnv(url);
  if (appEnv === 'local') {
    return 'webfrontend-localhost-logs';
  } else if (appEnv === 'dev') {
    return 'webfrontend-dev-logs';
  } else {
    return 'webfrontend-logs';
  }
}

function getEventName(errorType) {
  let eventName = 'webapp_error_';
  if (errorType === 'api') {
    eventName += 'api';
  } else if (errorType === 'client-side') {
    eventName += 'js';
  } else if (errorType === 'crash') {
    eventName += 'crash';
  } else {
    return null;
  }

  return `${eventName}_${getAppEnv()}`;
}

// Zoho ASAP help-widget throws that we do not want in Firestore/GA.
// August 2026 review: 19 of 39 production rows were `s.g[t].warning is not a
// function` or static.zohocdn.com chunk timeouts — not app crashes.
const ZOHO_NOISE_PATTERN =
  /warning is not a function|zohocdn\.com|desk\.zoho\.com|zoho\.com\/helpcenter|zoho\.com\/portal/i;

// Unhandled rejections are often plain objects. String(reason) becomes
// "[object Object]" and we lose the real failure. Prefer message / axios
// status / JSON so the report stays diagnosable.
export function formatErrorMessage(value) {
  if (value == null || value === '') {
    return 'Unknown error';
  }
  if (typeof value === 'string') {
    return value === '[object Object]'
      ? 'Unknown error (non-Error object)'
      : value;
  }
  if (value instanceof Error && value.message) {
    return value.message;
  }
  if (typeof value === 'object') {
    if (value.message) {
      return String(value.message);
    }
    if (value.response && value.response.status) {
      return `Request failed with status code ${value.response.status}`;
    }
    try {
      const json = JSON.stringify(value);
      if (json && json !== '{}') {
        return json;
      }
    } catch (err) {
      // Fall through to the generic object label.
    }
    return 'Unknown error (non-serializable object)';
  }
  return String(value);
}

function isNoisyThirdPartyError(errorEnvironment) {
  // Check message plus script URL/stack so Zoho errors are dropped even when
  // the message itself is generic (e.g. a chunk load timeout).
  const haystack = [
    errorEnvironment.message,
    errorEnvironment.file,
    errorEnvironment.filename,
    errorEnvironment.stack,
  ]
    .filter(Boolean)
    .join(' ');
  return ZOHO_NOISE_PATTERN.test(haystack);
}

export const firebaseErrorLogger = async (errorEnvironment) => {
  const message = formatErrorMessage(errorEnvironment.message);
  // Skip Zoho widget noise before Analytics and Firestore so both stay clean.
  if (isNoisyThirdPartyError({...errorEnvironment, message})) {
    return;
  }

  const db = getFirestore(app);
  const auth = getAuth(app);
  const analytics = getAnalytics(app);
  const browser = detect();
  const user = window.localStorage.getItem('zabble-user-name');
  const email = window.localStorage.getItem('zabble-email');

  const errorLogObject = {
    date: new Date(),
    // app_version: 'I1STJVX6UPHBOG',
    browser: browser.name + ' ' + browser.version,
    device: browser.os,
    user: user ? user : 'Error retrieving user data',
    email: email ? email : 'Error retrieving email data',
    route: errorEnvironment.route,
    message,
  };

  if (errorEnvironment.type === 'api') {
    errorLogObject.type = 'API error';
    errorLogObject.function = errorEnvironment.function;
    if (errorEnvironment.requestBody) {
      errorLogObject.requestBody = errorEnvironment.requestBody;
    }
  } else if (errorEnvironment.type === 'client-side') {
    errorLogObject.type = 'React / Client-side error';
  } else if (errorEnvironment.type === 'crash') {
    errorLogObject.type = 'Webapp crash';
    if (errorEnvironment.componentStack) {
      errorLogObject.componentStack = errorEnvironment.componentStack;
    }
  }

  // log to Analytics
  const eventName = getEventName(errorEnvironment.type);
  if (eventName) {
    const gaParams = {...errorLogObject};
    delete gaParams.requestBody;
    delete gaParams.componentStack;
    logEvent(analytics, eventName, gaParams);
  }

  // log to Firestore
  const collectionName = getFirebaseCollection(errorLogObject.route);

  try {
    await signInAnonymously(auth);
    await addDoc(collection(db, collectionName), errorLogObject);
  } catch (err) {
    console.log(err.message);
  }
};
